import type { FeePaymentDetail, PaymentProvider } from "@wallyo/contracts/console/payments";
import type { FormEvent } from "react";
import { t } from "../../i18n";
import { formatMoney } from "../../i18n/format";
import { Modal, WriteFailureNotice } from "../team/TeamDialogs";
import type { WriteFailure } from "../team/team-format";
import type { AnyPaymentActionKind, PaymentActionFields } from "./payment-actions";

export type PaymentDialogState = {
  kind: AnyPaymentActionKind;
  fields: PaymentActionFields;
  problem: string | null;
  failure: WriteFailure | null;
  pending: boolean;
};

export type PaymentActionDialogProps = {
  dialog: PaymentDialogState;
  /** The payment the correction is on; null when deciding a request from the approvals list. */
  payment: FeePaymentDetail | null;
  /** Who it is about and how much, for the title. */
  subject: string;
  /** The provider a reference is copied from: an Orange Money transaction id, a Stripe id. */
  provider: PaymentProvider | null;
  onFields: (fields: Partial<PaymentActionFields>) => void;
  onSubmit: () => void;
  onClose: () => void;
  onReload: () => void;
};

/** One money write: what it does, its fields, the reason, then send. Never shown as done before the server says so. */
export function PaymentActionDialog(props: PaymentActionDialogProps) {
  const { dialog, payment, provider } = props;
  const { kind, fields } = dialog;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };
  const needsReference = kind === "markPaid" || (kind === "settle" && fields.outcome === "done");
  const referenceLabel =
    provider === "orange_money" ? t("payments.field.orangeMoneyReference") : t("payments.field.reference");

  return (
    <Modal title={t(`payments.dialog.${kind}.title` as const, { subject: props.subject })} onClose={props.onClose}>
      <form className="modal-form" onSubmit={submit} noValidate data-dialog={kind}>
        <div className="modal-body">
          <p>{t(`payments.dialog.${kind}.body` as const)}</p>
        </div>

        {kind === "refund" || kind === "credit" ? (
          <div className="field">
            <label htmlFor="payment-amount">
              {t("payments.field.amount", { currency: payment?.currency ?? "GNF" })}
            </label>
            <input
              id="payment-amount"
              inputMode="decimal"
              value={fields.amount}
              onChange={(event) => props.onFields({ amount: event.target.value })}
            />
            {kind === "refund" && payment ? (
              <span className="meta">
                {t("payments.field.refundHint", { amount: formatMoney(payment.refundableAmount, payment.currency) })}
              </span>
            ) : null}
          </div>
        ) : null}

        {kind === "settle" ? (
          <fieldset className="pick-list">
            <legend>{t("payments.field.outcome")}</legend>
            {(["done", "failed"] as const).map((outcome) => (
              <label key={outcome}>
                <input
                  type="radio"
                  name="payment-outcome"
                  checked={fields.outcome === outcome}
                  onChange={() => props.onFields({ outcome })}
                />{" "}
                {t(`payments.field.outcome.${outcome}` as const)}
              </label>
            ))}
          </fieldset>
        ) : null}

        {needsReference ? (
          <div className="field">
            <label htmlFor="payment-reference">{referenceLabel}</label>
            <input
              id="payment-reference"
              className="mono"
              value={fields.providerReference}
              onChange={(event) => props.onFields({ providerReference: event.target.value })}
            />
            <span className="meta">{t("payments.field.referenceHint")}</span>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="payment-reason">{t("team.reason")}</label>
          <textarea
            id="payment-reason"
            rows={2}
            value={fields.reason}
            onChange={(event) => props.onFields({ reason: event.target.value })}
          />
          <span className="meta">{t("team.reasonHint")}</span>
        </div>

        {dialog.problem ? <p className="field-problem">{dialog.problem}</p> : null}
        {dialog.failure ? <WriteFailureNotice failure={dialog.failure} onReload={props.onReload} /> : null}
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={props.onClose}>
            {t("team.cancel")}
          </button>
          <button className={`btn ${kind === "reject" ? "danger" : "primary"}`} type="submit" disabled={dialog.pending}>
            {dialog.pending ? t("team.sending") : t(`payments.action.${kind}` as const)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
